import { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { Download, Upload, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, X, Plus } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

const PAGE_SIZE = 25;

interface Supplier {
    name: string;
    archived_at?: string | null;
    // Offered on Book In's "Choose Supplier" list (when it also has open
    // POs). Migration 181; defaults true.
    book_in_visible?: boolean;
    // Book In's duplicate stock check skips this supplier's products when
    // true (default). False = a SKU with stock already at the warehouse is
    // rejected, where the warehouse's "Check for duplicates" switch is on.
    // Migration 245.
    allow_duplicate_stock?: boolean;
    created_at?: string;
    updated_at?: string;
}

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
}

interface EditDraft {
    name: string;
    status: "active" | "archived";
    book_in: boolean;
    allow_duplicates: boolean;
}

const draftFromSupplier = (s: Supplier): EditDraft => ({
    name: s.name,
    status: s.archived_at ? "archived" : "active",
    book_in: s.book_in_visible !== false,
    allow_duplicates: s.allow_duplicate_stock !== false,
});

// `book_in` = yes|no: whether the supplier shows on Book In's supplier
// list. `allow_duplicates` = yes|no: whether Book In may receive a SKU of
// this supplier that already has stock at the warehouse. Blank on an
// update row leaves the column unchanged.
const COLUMNS = ["action", "name", "status", "book_in", "allow_duplicates"] as const;

// Supplier names occasionally read as numeric to Excel ("3M",
// "0001 Imports") — pin the column to Text so a download → upload
// round-trip doesn't mangle them into scientific notation.
const TEXT_COLUMNS = new Set<string>(["name"]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Suppliers");
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

const CHUNK_SIZE = 200;

const SuppliersAdmin = () => {
    const { t } = useTranslation();
    // Access-control read-only mode (ACCESS_CONTROL_PLAN.md §8.4). The
    // add/upload buttons and row-click edit are all mutation entry points.
    const readOnly = useReadOnly();
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [lastResult, setLastResult] = useState<BulkResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [search, setSearch] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(search);
    useEffect(() => { setSearchInput(search); }, [search]);
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(0);

    // Edit / add modal share the same draft state — `mode` distinguishes
    // a row-click (edit existing) from the Add Supplier button (create).
    const [mode, setMode] = useState<"edit" | "add" | null>(null);
    const [editing, setEditing] = useState<Supplier | null>(null);
    const [draft, setDraft] = useState<EditDraft | null>(null);
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => { setPage(0); }, [search, statusFilter]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("status", statusFilter || "all");
            if (search.trim()) params.set("q", search.trim());
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/suppliers?${params}`, {});
            if (!res.ok) throw new Error("load");
            const data = await res.json();
            setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
            setTotal(typeof data.total === "number" ? data.total : 0);
        } catch {
            toast.error(t("server_error"), { toastId: "suppliers-load-err" });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search, page, t]);

    useEffect(() => { refresh(); }, [refresh]);

    const openEdit = (s: Supplier) => {
        if (readOnly) return;
        setMode("edit");
        setEditing(s);
        setDraft(draftFromSupplier(s));
    };
    const openAdd = () => {
        if (readOnly) return;
        setMode("add");
        setEditing(null);
        setDraft({ name: "", status: "active", book_in: true, allow_duplicates: true });
    };
    const closeModal = () => {
        if (savingEdit) return;
        setMode(null);
        setEditing(null);
        setDraft(null);
    };
    const setField = <K extends keyof EditDraft>(key: K, value: EditDraft[K]) => {
        setDraft(prev => prev ? { ...prev, [key]: value } : prev);
    };

    // Save covers three flows:
    //   • Add  → action=add (status seeds archived_at on create)
    //   • Edit, name unchanged → action=update (status / book_in)
    //   • Edit, name changed   → action=rename then action=update
    // The BE bulk endpoint applies rows in order so rename runs first
    // and the subsequent update keys off the new name. Only the fields
    // that actually changed go on the update row; blank fields are left
    // alone by the BE.
    const saveEdit = async () => {
        if (!draft) return;
        const newName = draft.name.trim();
        if (!newName) {
            toast.error(t("server_error"), { toastId: "supplier-edit-noname" });
            return;
        }
        setSavingEdit(true);
        try {
            const rows: Record<string, unknown>[] = [];

            if (mode === "add") {
                rows.push({
                    action: "add",
                    name: newName,
                    status: draft.status,
                    book_in: draft.book_in ? "yes" : "no",
                    allow_duplicates: draft.allow_duplicates ? "yes" : "no",
                });
            } else if (editing) {
                const oldName = editing.name;
                const wasArchived = !!editing.archived_at;
                const nowArchived = draft.status === "archived";
                const nameChanged = newName !== oldName;
                const bookInChanged = draft.book_in !== (editing.book_in_visible !== false);
                const dupChanged = draft.allow_duplicates !== (editing.allow_duplicate_stock !== false);

                if (nameChanged) {
                    rows.push({ action: "rename", name: oldName, new_name: newName });
                }
                if (nowArchived !== wasArchived || bookInChanged || dupChanged) {
                    rows.push({
                        action: nowArchived && !wasArchived ? "remove" : "update",
                        name: newName,
                        status: nowArchived !== wasArchived ? (nowArchived ? "archived" : "active") : "",
                        book_in: bookInChanged ? (draft.book_in ? "yes" : "no") : "",
                        allow_duplicates: dupChanged ? (draft.allow_duplicates ? "yes" : "no") : "",
                    });
                }
                if (rows.length === 0) {
                    closeModal();
                    setSavingEdit(false);
                    return;
                }
            }

            const res = await apiFetch(`/suppliers/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rows }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result: BulkResult = await res.json();
            if (result.errors.length > 0) {
                toast.error(result.errors[0].reason, { toastId: `supplier-edit-${newName}` });
                return;
            }
            await refresh();
            toast.success(t("saved"), { toastId: `supplier-edit-ok-${newName}` });
            setMode(null);
            setEditing(null);
            setDraft(null);
        } catch {
            toast.error(t("server_error"), { toastId: `supplier-edit-err-${newName}` });
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDownloadTemplate = async () => {
        const example: string[][] = [
            ["add",    "Acme Ltd", "active", "yes", "yes"],
            ["update", "Acme Ltd", "archived", "", ""],
            ["update", "Acme Ltd", "active", "no", ""],
            ["update", "Acme Ltd", "", "", "no"],
            ["remove", "Acme Ltd", "", "", ""],
            ["rename", "Acme Ltd", "", "", ""],
        ];
        await downloadXlsx("suppliers-template.xlsx", COLUMNS, example);
    };

    const handleDownloadCurrent = async () => {
        // Page through the BE so the download covers every supplier matching
        // the current filters, not just the visible 25-row page slice.
        // BE caps page_size at 500 (suppliers controller listSuppliers).
        const DOWNLOAD_PAGE_SIZE = 500;
        const all: Supplier[] = [];
        try {
            const baseParams = new URLSearchParams();
            baseParams.set("status", statusFilter || "all");
            if (search.trim()) baseParams.set("q", search.trim());
            baseParams.set("page_size", String(DOWNLOAD_PAGE_SIZE));

            let pageNum = 0;
            while (true) {
                const params = new URLSearchParams(baseParams);
                params.set("page", String(pageNum));
                const res = await apiFetch(`/suppliers?${params}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const list: Supplier[] = Array.isArray(data.suppliers) ? data.suppliers : [];
                all.push(...list);
                const grandTotal = typeof data.total === "number" ? data.total : all.length;
                if (list.length === 0 || all.length >= grandTotal) break;
                pageNum += 1;
            }
        } catch {
            toast.error(t("server_error"), { toastId: "suppliers-download-err" });
            return;
        }

        const rows: string[][] = [];
        for (const s of all) {
            rows.push([
                "update",
                s.name,
                s.archived_at ? "archived" : "active",
                s.book_in_visible === false ? "no" : "yes",
                s.allow_duplicate_stock === false ? "no" : "yes",
            ]);
        }
        await downloadXlsx("suppliers.xlsx", COLUMNS, rows);
    };

    const handleUpload = async (file: File) => {
        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("suppliers_upload_parse_error"), { toastId: "suppliers-upload-parse" });
            return;
        }
        const { header, rows } = parsed;
        if (rows.length === 0) {
            toast.error(t("suppliers_upload_empty"), { toastId: "suppliers-upload-empty" });
            return;
        }
        for (const req of ["action", "name"]) {
            if (!header.includes(req)) {
                toast.error(t("suppliers_upload_bad_header"), { toastId: "suppliers-bad-header" });
                return;
            }
        }
        const objRows = rows.map(cells => {
            const obj: Record<string, string> = {};
            header.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
            return obj;
        });

        setUploading(true);
        setLastResult(null);
        const summary: BulkResult = { added: 0, updated: 0, removed: 0, errors: [] };

        try {
            for (let i = 0; i < objRows.length; i += CHUNK_SIZE) {
                const chunk = objRows.slice(i, i + CHUNK_SIZE);
                setProgress({ done: i, total: objRows.length });
                const res = await apiFetch(`/suppliers/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk }),
                });
                // 413 = chunk body exceeds the BE's JSON limit. Stop the
                // upload (subsequent chunks will fail the same way) and
                // surface a specific message so the operator knows it's
                // a size issue.
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "suppliers-upload-413" });
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const r: BulkResult = await res.json();
                summary.added += r.added;
                summary.updated += r.updated;
                summary.removed += r.removed;
                for (const e of r.errors) {
                    summary.errors.push({ row: e.row + i, reason: e.reason });
                }
            }
            setProgress({ done: objRows.length, total: objRows.length });
            setLastResult(summary);
            await refresh();
            toast.success(
                t("totes_bulk_summary", {
                    added: String(summary.added),
                    updated: String(summary.updated),
                    removed: String(summary.removed),
                }),
                { toastId: "suppliers-upload-ok" },
            );
        } catch {
            toast.error(t("server_error"), { toastId: "suppliers-upload-err" });
        } finally {
            setUploading(false);
            setTimeout(() => setProgress(null), 1500);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const visible = suppliers;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    if (loading) return <div className="admin-settings-loading">{t("loading")}</div>;

    return (
        <div className="products-admin">
            <div className="totes-admin-actions">
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={openAdd}
                    disabled={readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Plus size={14} />
                    {t("suppliers_add")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleDownloadTemplate}>
                    <Download size={14} />
                    {t("totes_download_template")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleDownloadCurrent}
                    disabled={suppliers.length === 0}
                >
                    <Download size={14} />
                    {t("totes_download_current")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Upload size={14} />
                    {uploading
                        ? (progress ? t("totes_upload_progress", { done: String(progress.done), total: String(progress.total) }) : t("submitting"))
                        : t("suppliers_upload_xlsx")}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: "none" }}
                    disabled={readOnly}
                    onChange={e => {
                        const f = e.target.files?.[0];
                        if (f && !readOnly) handleUpload(f);
                    }}
                />
            </div>

            {lastResult && (
                <div className="totes-admin-result">
                    <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-icon" />
                    <span>
                        {t("totes_bulk_summary", {
                            added: String(lastResult.added),
                            updated: String(lastResult.updated),
                            removed: String(lastResult.removed),
                        })}
                    </span>
                    {lastResult.errors.length > 0 && (
                        <details className="totes-admin-errors">
                            <summary>
                                <AlertCircle size={14} />
                                {lastResult.errors.length} {lastResult.errors.length === 1 ? "error" : "errors"}
                            </summary>
                            <ul>
                                {lastResult.errors.slice(0, 50).map((e, i) => (
                                    <li key={i}>
                                        {t("totes_row_label", { row: String(e.row) })}: {e.reason}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}

            <div className="locations-summary">
                <div className="locations-summary-total">
                    <span className="locations-summary-total-label">{t("suppliers_total")}</span>
                    <strong>{total}</strong>
                </div>
            </div>

            <FilterPills
                className="warehouse-admin-pills"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                    { value: "active",   label: t("suppliers_active") },
                    { value: "archived", label: t("suppliers_archived") },
                ]}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("suppliers_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearch(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== search.trim()) {
                            setSearch(searchInput.trim());
                        }
                    }}
                />
            </div>

            {total === 0 ? (
                <div className="locations-table-empty">{t("suppliers_no_matches")}</div>
            ) : (
                <>
                    <div className="locations-table-wrap products-admin-table-wrap">
                        <table className="locations-table products-admin-table">
                            <thead>
                                <tr>
                                    <th>{t("suppliers_col_name")}</th>
                                    <th style={{ width: 110 }}>{t("suppliers_col_status")}</th>
                                    <th style={{ width: 130 }}>{t("suppliers_col_book_in")}</th>
                                    <th style={{ width: 150 }}>{t("suppliers_col_allow_duplicates")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(s => (
                                    <tr
                                        key={s.name}
                                        className="products-admin-row"
                                        onClick={() => openEdit(s)}
                                    >
                                        <td className="products-admin-cell-truncate" title={s.name}>{s.name}</td>
                                        <td>
                                            <span className={`products-admin-status${s.archived_at ? " products-admin-status--archived" : ""}`}>
                                                {s.archived_at ? t("suppliers_archived") : t("suppliers_active")}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`products-admin-status${s.book_in_visible === false ? " products-admin-status--archived" : ""}`}>
                                                {s.book_in_visible === false ? t("suppliers_book_in_no") : t("suppliers_book_in_yes")}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`products-admin-status${s.allow_duplicate_stock === false ? " products-admin-status--archived" : ""}`}>
                                                {s.allow_duplicate_stock === false ? t("suppliers_allow_duplicates_no") : t("suppliers_allow_duplicates_yes")}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="locations-table-footer">
                        <span>{t("locations_showing_range", {
                            from: String(rangeFrom),
                            to: String(rangeTo),
                            total: String(total),
                        })}</span>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {mode && draft && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div
                        className="modal-card products-admin-modal suppliers-admin-modal"
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-labelledby="suppliers-edit-title"
                    >
                        <div className="modal-card-header products-admin-modal-header">
                            <div className="products-admin-modal-titles">
                                <h2 id="suppliers-edit-title" className="products-admin-modal-title">
                                    {mode === "add" ? t("suppliers_modal_add_title") : t("suppliers_modal_edit_title")}
                                </h2>
                            </div>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={closeModal}
                                disabled={savingEdit}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body products-admin-modal-body">
                            <div className="products-admin-form-grid">
                                <label className="dispatch-admin-form-label products-admin-form-label--full">
                                    {t("suppliers_modal_name_label")}
                                    <input
                                        className="dispatch-admin-form-input"
                                        value={draft.name}
                                        onChange={e => setField("name", e.target.value)}
                                        disabled={savingEdit}
                                        autoFocus={mode === "add"}
                                    />
                                </label>
                                <label className="dispatch-admin-form-label">
                                    {t("suppliers_col_status")}
                                    <select
                                        className="dispatch-admin-form-input"
                                        value={draft.status}
                                        onChange={e => setField("status", e.target.value as "active" | "archived")}
                                        disabled={savingEdit}
                                    >
                                        <option value="active">{t("suppliers_active")}</option>
                                        <option value="archived">{t("suppliers_archived")}</option>
                                    </select>
                                </label>
                                <label className="dispatch-admin-form-label dispatch-admin-form-label--check products-admin-form-label--full">
                                    <input
                                        type="checkbox"
                                        checked={draft.book_in}
                                        onChange={e => setField("book_in", e.target.checked)}
                                        disabled={savingEdit}
                                    />
                                    <span>{t("suppliers_book_in_label")}</span>
                                </label>
                                <label className="dispatch-admin-form-label dispatch-admin-form-label--check products-admin-form-label--full">
                                    <input
                                        type="checkbox"
                                        checked={draft.allow_duplicates}
                                        onChange={e => setField("allow_duplicates", e.target.checked)}
                                        disabled={savingEdit}
                                    />
                                    <span>{t("suppliers_allow_duplicates_label")}</span>
                                </label>
                            </div>
                        </div>
                        <div className="modal-confirm-buttons">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={closeModal}
                                disabled={savingEdit}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={saveEdit}
                                disabled={savingEdit || !draft.name.trim() || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {savingEdit ? t("saving") : t("save")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuppliersAdmin;
